/** Rutina domiciliaria — persistida como nota tipo RUTINA_FISIO (cualquier tenant fisio). */

import type { Nota } from './api';

export const RUTINA_FISIO_TIPO = 'RUTINA_FISIO';

export type RutinaEjercicio = {
  nombre: string;
  series: number | string;
  reps: number | string;
  hold: string;
  frecuencia: string;
  progresion: string;
};

export type RutinaFisio = {
  titulo: string;
  indicaciones: string;
  ejercicios: RutinaEjercicio[];
};

export const EJERCICIO_VACIO = (): RutinaEjercicio => ({
  nombre: '',
  series: 3,
  reps: 10,
  hold: '',
  frecuencia: '2×/día',
  progresion: '',
});

/** Plantillas genéricas — aplican a cualquier clínica de fisioterapia. */
export const PLANTILLAS_EJERCICIO_FISIO: RutinaEjercicio[] = [
  { nombre: 'Puente glúteo', series: 3, reps: 10, hold: '2 s', frecuencia: '2×/día', progresion: 'Subir reps si EVA < 4' },
  { nombre: 'Estiramiento isquiotibiales', series: 3, reps: 1, hold: '30 s', frecuencia: '2×/día', progresion: 'Sin dolor > 6/10 EVA' },
  { nombre: 'Pendulares Codman', series: 3, reps: 15, hold: '—', frecuencia: '3×/día', progresion: 'Aumentar amplitud gradual' },
  { nombre: 'Rotación externa con banda', series: 3, reps: 12, hold: '—', frecuencia: '1×/día', progresion: 'Subir resistencia de banda' },
  { nombre: 'Marcha activa', series: 1, reps: 1, hold: '10 min', frecuencia: '1×/día', progresion: '+2 min por semana' },
];

export function rutinaVacia(): RutinaFisio {
  return {
    titulo: 'Plan de ejercicios domiciliarios',
    indicaciones: '',
    ejercicios: [EJERCICIO_VACIO()],
  };
}

export function parseRutinaNota(n: Nota): RutinaFisio | null {
  if (n.tipo !== RUTINA_FISIO_TIPO) return null;
  try {
    const o = JSON.parse(n.contenido) as RutinaFisio;
    if (!o || !Array.isArray(o.ejercicios)) return null;
    return {
      titulo: o.titulo ?? 'Plan de ejercicios domiciliarios',
      indicaciones: o.indicaciones ?? '',
      ejercicios: o.ejercicios.map(e => ({ ...EJERCICIO_VACIO(), ...e })),
    };
  } catch {
    return null;
  }
}

export function serializeRutina(r: RutinaFisio): string {
  return JSON.stringify({
    titulo: r.titulo.trim() || 'Plan de ejercicios domiciliarios',
    indicaciones: r.indicaciones.trim(),
    ejercicios: r.ejercicios.filter(e => e.nombre.trim()),
  });
}

export function rutinaMasReciente(notas: Nota[]): RutinaFisio | null {
  const rutinas = notas
    .filter(n => n.tipo === RUTINA_FISIO_TIPO)
    .sort((a, b) => (b.registradaEn ?? '').localeCompare(a.registradaEn ?? ''));
  for (const n of rutinas) {
    const r = parseRutinaNota(n);
    if (r) return r;
  }
  return null;
}

export function notasEvolucionClinica(notas: Nota[]): Nota[] {
  return notas.filter(n => n.tipo !== RUTINA_FISIO_TIPO && n.tipo !== 'EVAL_FISIO' && n.tipo !== 'ODONTOGRAMA');
}

export function formatRutinaWhatsApp(
  rutina: RutinaFisio,
  pacienteNombre: string,
  opts?: {
    clinicaNombre?: string;
    fisioterapista?: string;
  },
): string {
  const clinica = opts?.clinicaNombre?.trim() || 'Clínica de fisioterapia';
  const lineas = [
    `🏃 *Rutina domiciliaria* — ${clinica}`,
    `Paciente: ${pacienteNombre}`,
    `Plan: ${rutina.titulo}`,
    '',
  ];
  rutina.ejercicios.filter(e => e.nombre.trim()).forEach((e, i) => {
    lineas.push(
      `*${i + 1}. ${e.nombre}*`,
      `   ${e.series} series × ${e.reps} reps${e.hold && e.hold !== '—' ? ` · hold ${e.hold}` : ''}`,
      `   Frecuencia: ${e.frecuencia || '—'}${e.progresion ? ` · ${e.progresion}` : ''}`,
      '',
    );
  });
  if (rutina.indicaciones.trim()) {
    lineas.push(`*Indicaciones:* ${rutina.indicaciones.trim()}`, '');
  }
  if (opts?.fisioterapista?.trim()) {
    lineas.push(`*Fisioterapista:* ${opts.fisioterapista.trim()}`, '');
  }
  lineas.push('Cualquier duda, responda a este mensaje. ¡Ánimo con la rehabilitación!');
  return lineas.join('\n');
}

/** Abre WhatsApp del paciente con la rutina (wa.me). */
export function whatsappEnlaceRutina(telefono: string | null | undefined, mensaje: string): string | null {
  const digits = (telefono ?? '').replace(/\D/g, '');
  if (digits.length < 8) return null;
  const num = digits.startsWith('502') ? digits : `502${digits.replace(/^0+/, '')}`;
  return `https://wa.me/${num}?text=${encodeURIComponent(mensaje)}`;
}
